import { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import useGlobalContext from "hooks/globalData";
import { useQuery } from "react-query";
import KpiTile from "./components/KpiTile";
import { monthKey, normalizeMonths, safeHttpUrl } from "./monthUtils";

const MEDALS = ["🥇", "🥈", "🥉"];
const AGE_COLOR: Record<string, "error" | "warning" | "success" | "info" | "default"> = {
  red: "error",
  yellow: "warning",
  green: "success",
  ready: "info",
};

// Merge bar = 2 approvals; shown as a neutral (X/2) counter.
const APPROVAL_TARGET = 2;

// Age buckets as the backend classifies them (ready = 3+ approvals, parity with
// the source). Red is expanded by default; the rest collapse.
const PR_SECTIONS: { key: string; title: string; expanded: boolean }[] = [
  { key: "red", title: "🔴 +5 days open — Needs Immediate Attention", expanded: true },
  { key: "yellow", title: "🟡 2–5 days open", expanded: false },
  { key: "green", title: "🟢 Less than 2 days open", expanded: false },
  { key: "ready", title: "🔵 Ready to Merge", expanded: false },
];

const OpenPrsSection = () => {
  const { developerDashboardService } = useGlobalContext();
  const q = useQuery("dashboardOpenPrs", () => developerDashboardService.getOpenPrs());
  const data = q.data?.data;

  if (q.isLoading) {
    return (
      <Box display="flex" justifyContent="center" my={6}>
        <CircularProgress />
      </Box>
    );
  }
  if (q.isError) {
    return <Alert severity="error">Failed to load open pull requests.</Alert>;
  }
  if (!data || data.totalOpen === 0) {
    return <Alert severity="info">No open KONE pull requests found.</Alert>;
  }

  const entries = data.entries ?? [];

  return (
    <Box>
      <Grid container columns={10} spacing={2} mb={3}>
        <Grid item xs={5} md={2}>
          <KpiTile label="Total Open" value={data.totalOpen} />
        </Grid>
        <Grid item xs={5} md={2}>
          <KpiTile label="🔴 >5 days" value={data.red} />
        </Grid>
        <Grid item xs={5} md={2}>
          <KpiTile label="🟡 2-5 days" value={data.yellow} />
        </Grid>
        <Grid item xs={5} md={2}>
          <KpiTile label="🟢 <2 days" value={data.green} />
        </Grid>
        <Grid item xs={5} md={2}>
          <KpiTile label="🔵 Ready" value={data.ready} />
        </Grid>
      </Grid>

      {PR_SECTIONS.map((sec) => {
        const list = entries.filter((p) => (p.ageGroup ?? "") === sec.key);
        if (list.length === 0) {
          return null;
        }
        return (
          <Accordion key={sec.key} defaultExpanded={sec.expanded} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {sec.title} ({list.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {list.map((p, i) => (
                <Paper key={`${p.repo}-${i}`} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
                    <Chip size="small" color={AGE_COLOR[p.ageGroup ?? ""] ?? "default"} label={`${p.daysOpen}d`} sx={{ fontWeight: 600 }} />
                    <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                      {p.repo}
                    </Typography>
                    <Typography component="span" variant="body2" color="text.secondary">
                      —
                    </Typography>
                    <Link href={safeHttpUrl(p.link)} target="_blank" rel="noopener" variant="body2" sx={{ fontWeight: 500 }}>
                      {p.title}
                    </Link>
                    {p.author && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        · 👤 {p.author}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
                    <Chip size="small" variant="outlined" label={`👀 approvals (${p.approvals ?? 0}/${APPROVAL_TARGET})`} />
                  </Box>
                </Paper>
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

const ReviewerRankingSection = () => {
  const { developerDashboardService } = useGlobalContext();
  const monthsQuery = useQuery("dashboardMonths", () => developerDashboardService.getAvailableMonths());
  const months = normalizeMonths(monthsQuery.data?.data);

  const [selectedKey, setSelectedKey] = useState<string>("");
  const lastMonth = months.at(-1);
  const latest = lastMonth ? monthKey(lastMonth) : "";
  const effectiveKey = selectedKey || latest;
  const [year, month] = effectiveKey ? effectiveKey.split("-").map(Number) : [0, 0];

  const q = useQuery(
    ["dashboardReviewers", year, month],
    () => developerDashboardService.getReviewerRanking(year, month),
    { enabled: year > 0 }
  );
  const data = q.data?.data;
  const ranking = data?.ranking ?? [];

  const renderRanking = () => {
    if (monthsQuery.isError) {
      return <Alert severity="error">Failed to load available months.</Alert>;
    }
    if (monthsQuery.isLoading || q.isLoading) {
      return (
        <Box display="flex" justifyContent="center" my={6}>
          <CircularProgress />
        </Box>
      );
    }
    if (q.isError) {
      return <Alert severity="error">Failed to load reviewer ranking.</Alert>;
    }
    if (ranking.length === 0) {
      return <Alert severity="info">No reviewer approvals found for this month.</Alert>;
    }
    return (
      <>
        <Grid container spacing={2} mb={2}>
          {ranking.slice(0, 3).map((r, i) => (
            <Grid item xs={12} md={4} key={r.name}>
              <KpiTile label={`${MEDALS[i]} ${r.name}`} value={`${r.approvals} approvals`} delta={r.uniquePrs} deltaSuffix=" PRs" />
            </Grid>
          ))}
        </Grid>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Reviewer</TableCell>
                <TableCell align="right">Approvals</TableCell>
                <TableCell align="right">Distinct PRs</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ranking.map((r) => (
                <TableRow key={r.name}>
                  <TableCell>{r.rank}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell align="right">{r.approvals}</TableCell>
                  <TableCell align="right">{r.uniquePrs}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <Box>
      <FormControl size="small" sx={{ minWidth: 220, mb: 3 }}>
        <InputLabel>Month</InputLabel>
        <Select label="Month" value={effectiveKey} onChange={(e) => setSelectedKey(String(e.target.value))}>
          {[...months].reverse().map((m) => (
            <MenuItem key={monthKey(m)} value={monthKey(m)}>
              {m.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {data && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          {data.totalKonePrs} KONE PRs created in this month
        </Typography>
      )}

      {renderRanking()}
    </Box>
  );
};

const PullRequestsTab = () => {
  const [sub, setSub] = useState(0);

  return (
    <Box>
      <Tabs value={sub} onChange={(_, value) => setSub(value)} sx={{ mb: 2 }}>
        <Tab label="Open PRs" />
        <Tab label="Reviewer Ranking" />
      </Tabs>
      {sub === 0 && <OpenPrsSection />}
      {sub === 1 && <ReviewerRankingSection />}
    </Box>
  );
};

export default PullRequestsTab;
